import { toPng } from 'html-to-image'
import jsPDF from 'jspdf'
import { DOPPEL_DARK } from '@/utils/style'

export const generatePDF = async (component) => {
  if (!component) {
    return
  }

  // Wait for fonts and styles to load
  await document.fonts.ready

  // Ensure all images in the dashboard element are loaded
  const waitForImagesToLoad = (element: HTMLElement): Promise<void[]> => {
    const images = element.querySelectorAll('img') as NodeListOf<HTMLImageElement>
    const promises = Array.from(images).map((img) => {
      if (img.complete) {
        return Promise.resolve()
      }
      return new Promise<void>((resolve, reject) => {
        img.onload = () => resolve()
        img.onerror = () => reject(new Error(`Image failed to load: ${img.src}`))
      })
    })
    return Promise.all(promises)
  }

  await waitForImagesToLoad(component)

  // Add additional wait for animations
  await new Promise((resolve) => setTimeout(resolve, 1000))

  const scaleFactor = 0.8 // Can't result in file that exceeds SendGrid's 30MB attachment limit
  const width = component.offsetWidth * scaleFactor
  const height = component.offsetHeight * scaleFactor

  const imageData = await toPng(component, {
    quality: 1,
    backgroundColor: DOPPEL_DARK,
    width, // Scaled width
    height, // Scaled height
    style: {
      transform: `scale(${scaleFactor})`,
      transformOrigin: 'top left',
    },
    filter: (node) => {
      // Exclude nodes with data-ignore-png="true"
      return !(node.getAttribute && node.getAttribute('data-ignore-png') === 'true')
    },
  })

  // Initialize jsPDF with scaled dimensions
  const pdf = new jsPDF({
    orientation: 'landscape',
    unit: 'px',
    format: [width / scaleFactor, height / scaleFactor], // Original dimensions for correct PDF rendering
  })

  // Add the captured high-resolution image to the PDF
  pdf.addImage(imageData, 'PNG', 0, 0, width / scaleFactor, height / scaleFactor)

  return pdf
}
